/**
 * What a price figure MEANS — the two questions the rail and the package card
 * each ask before they put a word above a number.
 *
 * These lived inline in `_booking-sidebar.tsx` and `_packages/_package-card.tsx`
 * as one-line predicates. They are here because every bug this page has had in
 * its price column was the same shape — a LABEL and a FIGURE answering
 * different questions — and an inline predicate cannot be exhaustively tested
 * against the matrix of category kinds, party shapes and availability states
 * that actually reaches it. `_price-presentation.test.ts` runs that matrix.
 *
 * Pure and i18n-free, like `_capacity.ts` beside it and for the same reason:
 * the booking widget, the package cards and anything added later must reach one
 * answer, not three that drift.
 */

import { sumPax, type PaxCategory } from "./_capacity";

/** The rail→bar transport type. DEFINED IN `lib/`, not here: the pinned price
 *  bar is a shared component, and a shared component importing a type out of
 *  one route's private folder is the dependency pointing the wrong way.
 *  Re-exported so this file stays the one import for the page's price
 *  presentation. */
export type { PartyTotals } from "@/lib/party-totals";

/** Counts a traveller has selected, keyed by category id. */
export type SelectedCounts = Record<string, number>;

/**
 * Is the figure a PARTY TOTAL, or one thing's price?
 *
 * Head count, which is the guide's own rule (`if (n < 2)`) — and, once
 * `paxFor` stopped returning zero heads for a group of unknown size, the only
 * count needed. Heads are now always >= priced steps, so a second arm counting
 * steps could never fire.
 *
 * It is worth saying why that arm existed, because the shape of the mistake
 * matters more than the line. A `per_group` row with no `unitFits` used to
 * count as ZERO travellers, so Adult x1 beside one of those was one head and
 * two charges, and the rail called a two-line total a from-price. The first fix
 * counted steps INSTEAD of heads — which corrected that case and broke the two
 * an operator can actually configure, because one Couple (`per_group`,
 * `unitFits: 2`) and one Cabin (`per_unit`, `unitFits: 4`) are single steps
 * carrying whole parties. The real defect was never here: it was `paxFor`
 * reporting a line with people on it as nobody.
 */
export function showsPartyTotal(
  categories: PaxCategory[],
  counts: SelectedCounts,
): boolean {
  return sumPax(categories, counts) > 1;
}

/** Why a package card is showing a from-price instead of a party total. */
export type FromPriceReason = "sold-out" | "below-minimum";

/**
 * Should the card quote where prices START rather than this party's total?
 *
 * True whenever the total on screen is not one anybody can actually buy, and
 * the figure, its label, the struck "was", the saving, the discount flag and
 * the group breakdown all follow this single answer — because letting them
 * decide separately is how "From" came to sit over a party total.
 *
 *  - `sold-out` — nothing is purchasable at any party size.
 *  - `below-minimum` — the package needs more travellers than are selected, so
 *    the figure changes the moment they qualify. The guide prices this state
 *    the same way ("From ... per person, 4 travelers or more").
 *
 * Returns null when the card is quoting a real, bookable total.
 *
 * An OVER-CAPACITY party is deliberately not a reason. Too MANY people for the
 * seats left is the opposite problem: that total is a genuine quote for a party
 * the traveller chose, and the fix is to remove people, not to reframe the
 * price.
 */
export function fromPriceReason(input: {
  isSoldOut: boolean;
  minParticipants: number | null;
  categories: PaxCategory[];
  counts: SelectedCounts;
}): FromPriceReason | null {
  if (input.isSoldOut) return "sold-out";
  const { minParticipants } = input;
  if (
    minParticipants != null &&
    sumPax(input.categories, input.counts) < minParticipants
  ) {
    return "below-minimum";
  }
  return null;
}
